import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TaskDataService } from '../../../../shared/services/task-data.service';
import { ProfileDataService } from '../../../../shared/services/profile-data.service';
import * as FandCActions from './fandc-generator.actions';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';
import { Store } from '@ngrx/store';
import notify from 'devextreme/ui/notify';
import { getNotificationTimeout } from '../../../../types/resource';
import {
  selectedLoanNumber,
  selectedFrequency,
  selectFilteredPrototypeTasks,
  selectMaturityDate,
  selectPrototypeTasks,
  selectReferenceDate,
  selectFrequencyOffsetValue,
  selectFirstDueDate,
  selectLoanDetail,
  selectGeneratedTasks,
  selectSelectedFormatOption,
  selectSelectedReportEntity,
  selectSelectedEntityName,
  selectReportEntityOptions,
  selectTemplateTypes,
  selectSelectedTemplateType,
  selectTemplates,
  selectRootTemplate,
  selectSelectedPrototypeTasks,
  selectTaskId,
  selectTaskGenerationMetadataId,
  selectSelectedLoanDetail,
  selectLoans,
  selectExistingTaskIds,
  selectSelectedReviewerId,
  selectSelectedReviewerName,
} from './fandc-generator.selectors';
import { IGeneratedTasksModel, ITaskRequest, TaskRequest } from '../../../../modules/tasks/model/types';
import { Router } from '@angular/router';
import { Frequency, FrequencyOptionsService } from '../services/frequencyoptions.service';


@Injectable()
export class FandCGeneratorEffects {
  private actions$ = inject(Actions);
  private taskDataService = inject(TaskDataService);
  private profileDataService = inject(ProfileDataService, { optional: true }) as ProfileDataService;
  private store = inject(Store);
  private router = inject(Router);
  private frequencyOptionsService = inject(FrequencyOptionsService);

  constructor() {}

  fetchReviewers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchReviewers),
      switchMap(() => {
        if (!this.profileDataService) {
          return of(FandCActions.fetchReviewersSuccess({ reviewers: [] }));
        }
        return this.profileDataService.getUsersByGroup(['Asset Management']).pipe(
          map((response: any) => {
            // ApiService.get() returns _.values({message, result}) = [messageString, resultArray]
            // resultArray = [{ groupName: string, users: { id: string, name: string }[] }]
            const groups: any[] = Array.isArray(response?.[1]) ? response[1] : [];
            const reviewers = groups
              .flatMap((g: any) => Array.isArray(g.users) ? g.users : [])
              .map((u: any) => ({ id: u.id, name: u.name }));
            return FandCActions.fetchReviewersSuccess({ reviewers });
          }),
          catchError(() => of(FandCActions.fetchReviewersSuccess({ reviewers: [] })))
        );
      })
    )
  );

  private normalizeText(value: any): string {
    return (value ?? '')
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  private getLoanId(loan: any): string | null {
    if (!loan || typeof loan !== 'object') {
      return null;
    }

    if (Array.isArray(loan)) {
      return loan.length > 0 ? this.getLoanId(loan[0]) : null;
    }

    // console.log('[DEBUG] getLoanId - Full loan object:', JSON.stringify(loan, null, 2));
    // console.log('[DEBUG] getLoanId - All loan keys:', Object.keys(loan));

    const directId = (
      loan.id ??
      loan.Id ??
      loan.facilityId ??
      loan.FacilityId ??
      loan.loanId ??
      loan.LoanId ??
      loan.value ??
      loan.Value ??
      loan.facilityDetail?.id ??
      loan.FacilityDetail?.Id ??
      loan.facility?.id ??
      loan.Facility?.Id ??
      null
    );

    if (directId) {
      // console.log('[DEBUG] Found direct ID:', directId);
      return directId;
    }

    const dynamicIdKey = Object.keys(loan).find((key) => /id$/i.test(key));
    if (dynamicIdKey) {
      const dynamicId = loan[dynamicIdKey];
      if (dynamicId) {
        // console.log('[DEBUG] Found dynamic ID via key:', dynamicIdKey, '=', dynamicId);
        return dynamicId;
      }
    }

    // As a last resort, try to extract numeric ID from loan name (e.g., "240006001 - Sutton" → "240006001")
    const loanName = this.getLoanName(loan);
    if (loanName) {
      const match = loanName.match(/^(\d+)/);
      if (match && match[1]) {
        // console.log('[DEBUG] Extracted numeric ID from loan name:', match[1]);
        return match[1];
      }
    }

    // console.log('[DEBUG] No loan ID found - loan object has no ID property');
    return null;
  }

  private getLoanName(loan: any): string | null {
    if (!loan) {
      return null;
    }

    if (typeof loan === 'string') {
      return loan;
    }

    if (Array.isArray(loan)) {
      return loan.length > 0 ? this.getLoanName(loan[0]) : null;
    }

    return (
      loan.name ??
      loan.Name ??
      loan.loanNumber ??
      loan.LoanNumber ??
      loan.referenceName ??
      loan.ReferenceName ??
      loan.facilityName ??
      loan.FacilityName ??
      loan.facilityDetail?.name ??
      loan.FacilityDetail?.Name ??
      loan.displayName ??
      loan.DisplayName ??
      null
    );
  }

  private getTemplateIdFromSelectedType(templateTypes: any[], selectedType: string | null): string | null {
    if (!selectedType || !Array.isArray(templateTypes)) {
      return null;
    }

    const matchedTemplateType = templateTypes.find((item: any) => item?.templateType === selectedType);
    return (
      matchedTemplateType?.templateId ??
      matchedTemplateType?.id ??
      matchedTemplateType?.TemplateId ??
      matchedTemplateType?.Id ??
      null
    );
  }

  private findFacilityIdByLoanName(loans: any[], selectedLoanName: string | null): string | null {
    if (!Array.isArray(loans) || !selectedLoanName) {
      return null;
    }

    const normalizedSelectedLoanName = this.normalizeText(selectedLoanName);
    const selectedLoanToken = normalizedSelectedLoanName.split('-')[0]?.trim();

    const matchedLoan = loans.find((loan: any) => {
      const candidateNames = [
        this.getLoanName(loan),
        loan?.name,
        loan?.Name,
        loan?.loanNumber,
        loan?.LoanNumber,
        loan?.referenceName,
        loan?.ReferenceName,
      ];

      const normalizedCandidates = candidateNames
        .filter((name) => name != null)
        .map((name) => this.normalizeText(name));

      const exactMatch = normalizedCandidates.some((name) => name === normalizedSelectedLoanName);
      if (exactMatch) {
        return true;
      }

      const containsMatch = normalizedCandidates.some(
        (name) => name.includes(normalizedSelectedLoanName) || normalizedSelectedLoanName.includes(name)
      );
      if (containsMatch) {
        return true;
      }

      if (selectedLoanToken) {
        return normalizedCandidates.some(
          (name) => name.startsWith(selectedLoanToken) || name.includes(selectedLoanToken)
        );
      }

      return false;
    });

    return this.getLoanId(matchedLoan);
  }

  private resolveReportEntityValue(reportEntity: any, reportEntityLabel: any): string | null {
    const toScalar = (value: any): string | null => {
      if (value == null) {
        return null;
      }

      if (typeof value === 'string' || typeof value === 'number') {
        const stringValue = value.toString().trim();
        return stringValue.length > 0 ? stringValue : null;
      }

      if (typeof value === 'object') {
        const candidate =
          value.value ??
          value.label ??
          value.name ??
          value.type ??
          value.reportEntity ??
          value.reportEntityType ??
          value.reportEntityLabel ??
          null;

        if (candidate != null) {
          const candidateValue = candidate.toString().trim();
          return candidateValue.length > 0 ? candidateValue : null;
        }
      }

      return null;
    };

    const reportEntityScalar = toScalar(reportEntity);
    const reportEntityLabelScalar = toScalar(reportEntityLabel);
    const normalizedValue = this.normalizeText(reportEntityScalar);
    const normalizedLabel = this.normalizeText(reportEntityLabelScalar);

    const validValues = [
      'borrowers',
      'guarantors',
      'jointVentures',
      'lenders',
      'loanParties',
      'masterTenants',
      'operatingLessees',
      'properties',
    ];

    const normalizedToValueMap: Record<string, string> = {
      borrower: 'borrowers',
      borrowers: 'borrowers',
      guarantor: 'guarantors',
      guarantors: 'guarantors',
      'joint venture': 'jointVentures',
      'joint ventures': 'jointVentures',
      lender: 'lenders',
      lenders: 'lenders',
      'loan party': 'loanParties',
      'loan parties': 'loanParties',
      'master tenant': 'masterTenants',
      'master tenants': 'masterTenants',
      'operating lessee': 'operatingLessees',
      'operating lessees': 'operatingLessees',
      property: 'properties',
      properties: 'properties',
    };

    if (reportEntityScalar && validValues.includes(reportEntityScalar)) {
      return reportEntityScalar;
    }

    if (normalizedValue && normalizedToValueMap[normalizedValue]) {
      return normalizedToValueMap[normalizedValue];
    }

    if (normalizedLabel && normalizedToValueMap[normalizedLabel]) {
      return normalizedToValueMap[normalizedLabel];
    }

    return null;
  }

  private getPrototypeTaskField(task: any, candidateKeys: string[]): any {
    if (!task || typeof task !== 'object') {
      return null;
    }

    for (const key of candidateKeys) {
      if (task[key] != null && task[key] !== '') {
        return task[key];
      }
    }

    const normalizedCandidates = candidateKeys.map((key) => this.normalizeText(key));
    const matchingKey = Object.keys(task).find((existingKey) => {
      const normalizedExistingKey = this.normalizeText(existingKey);
      return normalizedCandidates.includes(normalizedExistingKey);
    });

    if (matchingKey && task[matchingKey] != null && task[matchingKey] !== '') {
      return task[matchingKey];
    }

    const taskData = task.taskData;
    if (taskData != null) {
      try {
        const parsedTaskData = typeof taskData === 'string' ? JSON.parse(taskData) : taskData;
        if (parsedTaskData && typeof parsedTaskData === 'object') {
          for (const key of candidateKeys) {
            if (parsedTaskData[key] != null && parsedTaskData[key] !== '') {
              return parsedTaskData[key];
            }
          }

          const parsedMatchingKey = Object.keys(parsedTaskData).find((existingKey) => {
            const normalizedExistingKey = this.normalizeText(existingKey);
            return normalizedCandidates.includes(normalizedExistingKey);
          });

          if (
            parsedMatchingKey &&
            parsedTaskData[parsedMatchingKey] != null &&
            parsedTaskData[parsedMatchingKey] !== ''
          ) {
            return parsedTaskData[parsedMatchingKey];
          }
        }
      } catch {
        // Ignore malformed taskData and continue with default behavior.
      }
    }

    return null;
  }

  private getFirstPrototypeTaskField(tasks: any[], candidateKeys: string[]): any {
    if (!Array.isArray(tasks) || tasks.length === 0) {
      return null;
    }

    for (const task of tasks) {
      const value = this.getPrototypeTaskField(task, candidateKeys);
      if (value != null && value !== '') {
        return value;
      }
    }

    return null;
  }

  fetchTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchTemplates),
      switchMap(({ criteria }) =>
        this.taskDataService.getTaskTemplates(false).pipe(
          map((response) => {
            // console.log('fetchTemplates response', response);
            const filteredData = Array.isArray(response[1])
              ? response[1].filter(
                  (item) =>
                    !criteria.referenceTypeName ||
                    (item.referenceType === criteria.referenceTypeName && item.templateType === criteria.templateName)
                )
              : [];
              // console.log('filteredData', filteredData);
            return FandCActions.fetchTemplatesSuccess({ data: filteredData });
          }),
          catchError(() => of(FandCActions.fetchTemplatesSuccess({ data: [] })))
        )
      )
    )
  );

  fetchRootTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchRootTemplate),
      switchMap(({ templateName }) =>
        this.taskDataService.getTaskTemplatesByType(templateName).pipe(
          map((response) => {

            const filteredData = Array.isArray(response[1])
              ? response[1].filter(
                  (item) =>
                    item.templateType === templateName
                )
              : [];

            return FandCActions.fetchRootTemplateSuccess({ data: filteredData.length > 0 ? filteredData[0] : null });
          }),
          catchError(() => of(FandCActions.fetchRootTemplateSuccess({ data: null })))
        )
      )
    )
  );

  fetchTemplatesTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchTemplateTypes),
      switchMap(({ criteria }) =>
        this.taskDataService.getTaskTemplates(false).pipe(
          map((response) => ({
            referenceType: criteria.referenceTypeName,
            response: response,
          })),
          catchError(() => of({ referenceType: criteria.referenceTypeName, response: null }))
        )
      ),
      map((result) => {
        if (result && result.response) {
          const filteredData = Array.isArray(result.response[1])
            ? result.response[1].filter(
                (item) =>
                  !result.referenceType ||
                  item.referenceType === result.referenceType
              )
            : [];
          return { data: filteredData };
        } else {
          return { data: [] };
        }
      }),
      map((result) => {
        if (result && Array.isArray(result.data)) {
          // Extract unique template types and preserve their GUID for downstream saves.
          const uniqueTemplateTypes = Array.from(
            new Map(
              result.data.map((item) => [
                item.templateType,
                {
                  templateType: item.templateType,
                  templateId: item.id ?? item.templateId ?? null,
                },
              ])
            ).values()
          );

          return FandCActions.fetchTemplateTypesSuccess({
            data: uniqueTemplateTypes,
          });
        }
        return FandCActions.fetchTemplateTypesSuccess({ data: [] });
      })
    )
  );

  fetchDefaultTasksForTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchDefaultTasksForTemplate),
      withLatestFrom(
        this.store.select(selectTemplates),
      ),
      switchMap(([ action, templates ]) =>
      {
        // Find the template that matches the criteria. During metadata-driven edit
        // this action can fire before templates are fully loaded.
        const matchingTemplate = templates.find((t: any) =>
          t?.name === action.criteria.templateName ||
          t?.templateName === action.criteria.templateName
        );
        const templateId = matchingTemplate?.id ?? matchingTemplate?.templateId ?? null;

        if (!templateId) {
          return this.taskDataService.getTaskTemplates(false).pipe(
            switchMap((response) => {
              const fallbackTemplates = Array.isArray(response?.[1]) ? response[1] : [];
              const fallbackTemplate = fallbackTemplates.find((t: any) =>
                t?.name === action.criteria.templateName ||
                t?.templateName === action.criteria.templateName
              );
              const fallbackTemplateId = fallbackTemplate?.id ?? fallbackTemplate?.templateId ?? null;

              if (!fallbackTemplateId) {
                return of(
                  FandCActions.fetchDefaultTasksForTemplateSuccess({
                    data: {
                      templateName: action.criteria.templateName,
                      templateId: null,
                      tasks: [],
                    },
                  })
                );
              }

              return this.taskDataService.getDefaultTasksForTemplate(fallbackTemplateId).pipe(
                map((result) => {
                  const returnedData = result[1] ?? { templateName: action.criteria.templateName, templateId: null, tasks: [] };
                  return FandCActions.fetchDefaultTasksForTemplateSuccess({
                    data: returnedData,
                  });
                }),
                catchError(() => of(FandCActions.fetchDefaultTasksForTemplateSuccess({ data: { templateName: action.criteria.templateName, templateId: null, tasks: [] } })))
              );
            }),
            catchError(() => of(FandCActions.fetchDefaultTasksForTemplateSuccess({ data: { templateName: action.criteria.templateName, templateId: null, tasks: [] } })))
          );
        }

        return this.taskDataService
          .getDefaultTasksForTemplate(templateId)
          .pipe(
            map((result) => {
              const returnedData = result[1] ?? { templateName: action.criteria.templateName, templateId, tasks: [] };
              return FandCActions.fetchDefaultTasksForTemplateSuccess({
                data: returnedData,
              });
            }),
            catchError(() => of(FandCActions.fetchDefaultTasksForTemplateSuccess({ data: { templateName: action.criteria.templateName, templateId, tasks: [] } })))
          );
        }
      )
    )
  );

  setLoanNumber$ = createEffect(() =>
  this.actions$.pipe(
    ofType(FandCActions.setLoanNumber),
    withLatestFrom(this.store.select(selectLoans)),
    switchMap(([{ selectedLoan }, loans]) => {
      // console.log('[DEBUG] setLoanNumber fired with:', {
      //   selectedLoan,
      //   loansCount: Array.isArray(loans) ? loans.length : 0,
      //   firstLoanName: Array.isArray(loans) && loans.length > 0 ? loans[0]?.name : 'N/A',
      // });

      const selectedLoanId = this.getLoanId(selectedLoan);
      const selectedLoanName = this.getLoanName(selectedLoan);

      if (!selectedLoanId && !selectedLoanName) {
        return EMPTY;
      }

      const resolveWithLoans = (loanList: any[]) => {
        const facilityId = selectedLoanId || this.findFacilityIdByLoanName(loanList, selectedLoanName);

        // console.log('[DEBUG] setLoanNumber resolved:', {
        //   selectedLoanId,
        //   selectedLoanName,
        //   facilityId,
        // });

        return facilityId
          ? of(FandCActions.fetchLoanDetail({ facilityId }))
          : EMPTY;
      };

      if (selectedLoanId) {
        return of(FandCActions.fetchLoanDetail({ facilityId: selectedLoanId }));
      }

      if (Array.isArray(loans) && loans.length > 0) {
        return resolveWithLoans(loans);
      }

      return this.taskDataService.getLoanNumbersWithDeal().pipe(
        switchMap((response) => {
          const freshLoans = Array.isArray(response?.[1]) ? response[1] : [];
          return resolveWithLoans(freshLoans);
        }),
        catchError(() => EMPTY)
      );
    })
  )
);

  fetchLoans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchLoans),
      switchMap(() =>
        this.taskDataService.getLoanNumbersWithDeal().pipe(
          map((response) => {
            const data = Array.isArray(response[1]) ? response[1] : [];
            return FandCActions.fetchLoansSuccess({ data });
          }),
          catchError(() => of(FandCActions.fetchLoansSuccess({ data: [] })))
        )
      )
    )
  );

    fetchReportEntityOptions$ = createEffect(() =>
      this.actions$.pipe(
        ofType(FandCActions.fetchReportEntityOptions),
        map(() => {
          const data = [
            { label: 'Borrower', value: 'borrowers' },
            { label: 'Guarantor', value: 'guarantors' },
            { label: 'Joint Venture', value: 'jointVentures' },
            { label: 'Lender', value: 'lenders' },
            { label: 'Loan Parties', value: 'loanParties' },
            { label: 'Master Tenant', value: 'masterTenants' },
            { label: 'Operating Lessee', value: 'operatingLessees' },
            { label: 'Property', value: 'properties' },
          ];
          return FandCActions.fetchReportEntityOptionsSuccess({ data });
        })
      )
    );

  fetchLoanDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchLoanDetail),
      withLatestFrom(this.store.select(selectSelectedLoanDetail)),
      switchMap(([{ facilityId }, existingLoanDetail]) => {
        if (!facilityId) {
          return existingLoanDetail
            ? of(FandCActions.fetchLoanDetailSuccess({ data: existingLoanDetail }))
            : EMPTY;
        }
        // console.log('[DEBUG] fetchLoanDetail fired with facilityId:', facilityId);
        return this.taskDataService.getLoanDetail(facilityId).pipe(
          map((response) => {
            // console.log('[DEBUG] fetchLoanDetail received response:', response);
            const normalizedLoanDetail = Array.isArray(response?.[1])
              ? response[1][0] ?? null
              : response?.[1] ?? null;
            return FandCActions.fetchLoanDetailSuccess({
              data: normalizedLoanDetail ?? existingLoanDetail ?? null,
            });
          }),
          catchError(() =>
            existingLoanDetail
              ? of(FandCActions.fetchLoanDetailSuccess({ data: existingLoanDetail }))
              : EMPTY
          )
        );
      })
    )
  );

  generateTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.generateTasks),
      withLatestFrom(
        this.store.select(selectSelectedPrototypeTasks),
        this.store.select(selectReferenceDate),
        this.store.select(selectMaturityDate),
        this.store.select(selectedLoanNumber),
        this.store.select(selectedFrequency),
        this.store.select(selectFirstDueDate),
        this.store.select(selectSelectedFormatOption),
        this.store.select(selectSelectedEntityName),
        this.store.select(selectSelectedReviewerId),
        this.store.select(selectSelectedReviewerName),
      ),
      switchMap(
        ([
          action,
          prototypeTasks,
          referenceDate,
          maturityDate,
          loanNumber,
          frequency,
          firstDueDate,
          selectedFormatOption,
          selectedEntityName,
          selectedReviewerId,
          selectedReviewerName,
        ]) => {
          // Group prototype tasks by firstDueDate and frequency
          const taskGroups: { [key: string]: any[] } = {};


          if (Array.isArray(prototypeTasks)) {
            prototypeTasks.forEach((task: any) => {
              // Create a key based on reference date and frequency and entity name
              const taskDueDate = task.referenceDate || referenceDate;
              const taskUntilDate = task.untilDate || maturityDate;
              const taskFrequency = task.frequency || frequency;
              const taskEntityName = task.entityName || selectedEntityName;
              const groupKey = `${taskDueDate}_${taskUntilDate}_${taskFrequency}_${taskEntityName}`;


              if (!taskGroups[groupKey]) {
                taskGroups[groupKey] = [];
              }


              taskGroups[groupKey].push(task);
            });
          }

          // console.log('taskGroups', taskGroups);

          // Create an array of IGeneratedTasksModel objects
          const generatedTasksModels: IGeneratedTasksModel[] = [];


          // Process each group of tasks
          Object.entries(taskGroups).forEach(([groupKey, groupTasks]) => {
            const [groupDueDate, groupFrequencyStr] = groupKey.split('_');


            // Map tasks for this group
            const mappedTasks = groupTasks.map((task: any) => {
              const taskData: any = {
                loanNumber: loanNumber,
              };


              if (selectedFormatOption != null && selectedFormatOption !== '') {
                taskData.format = selectedFormatOption;
              }


              if (task.notes != null && task.notes !== '') {
                taskData.notes = task.notes;
              }

              if (task.citation != null && task.citation !== '') {
                taskData.citation = task.citation;
              }


              if (task.reference != null && task.reference !== '') {
                taskData.reference = task.reference;
              }

              if (task.reportEntity != null && task.reportEntity !== '') {
                taskData.reportEntity = task.reportEntity;
              }

              if (task.reportEntityLabel != null && task.reportEntityLabel !== '') {
                taskData.reportEntityLabel = task.reportEntityLabel;
              }


              if (task.entityName != null && task.entityName !== '') {
                taskData.entityName = task.entityName;
              }


              // console.log('taskData', taskData);


              return {
                id: task.id,
                taskName: task.taskName,
                description: task.description,
                dueDate: task.dueDate,
                assignedTo: task.assignedTo,
                assignedUserName: task.assignedUserName,
                taskConfigurationId: task.taskConfigurationId,
                team: task.team,
                templateId: task.templateId,
                taskData: JSON.stringify(taskData),
              };
            });


            // Ensure we're using the correct frequency type by using the sample's frequency
            // This assumes all tasks in the group have the same frequency
            const firstTask = groupTasks[0];
            const groupFrequency = firstTask?.frequency || frequency;
            const tenor = this.frequencyOptionsService.getTenor(groupFrequency);
            const groupReferenceDate = firstTask?.referenceDate || referenceDate;
            const groupUntilDate = firstTask?.untilDate ?? maturityDate ?? null;

            // Create an IGeneratedTasksModel for this group
            const groupModel: IGeneratedTasksModel = {
              assignedUserName: null,
              approverGroup: null,
              secondApproverGroup: null,
              thirdApproverGroup: null,
              team: null,
              referenceTypeId: '00000000-0000-0000-0000-000000000000', // empty GUID
              tasks: mappedTasks,
              referenceType: null,
              referenceId: '00000000-0000-0000-0000-000000000000', // empty GUID
              reference: loanNumber,
              referenceName: loanNumber,
              referenceDate: groupReferenceDate,
              tenor: tenor,
              frequency: null,
              numberOfCycles: null,
              untilDate: groupUntilDate,
              dateRollConvention: 'Following',
              location: 'US-GA',
              workflowId: null,
              referenceDateIsFirstDueDate: true,
              // Top-level batch override for the "Review" task's assignment (FAL-1237).
              // Must not be placed on individual task objects in `tasks`.
              reviewerId: selectedReviewerId ?? null,
              reviewerName: selectedReviewerName ?? null,
            };


            generatedTasksModels.push(groupModel);
          });

          // console.log('generatedTasksModels', generatedTasksModels);



          // Call the service with the array of models
          return this.taskDataService.getGeneratedTasks(generatedTasksModels).pipe(
            map((result) => {
              // console.log('result', result);
              const returnedData = result[1] ?? [];
              return FandCActions.fetchGeneratedTasksSuccess({
                tasks: returnedData,
              });
            })
          );
        }
      )
    )
  );

  saveGeneratedTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.saveGeneratedTasks),
      withLatestFrom(
        this.store.select(selectGeneratedTasks),
        this.store.select(selectSelectedPrototypeTasks),
        this.store.select(selectRootTemplate),
        this.store.select(selectTemplateTypes),
        this.store.select(selectSelectedTemplateType),
        this.store.select(selectTaskGenerationMetadataId),
        this.store.select(selectExistingTaskIds),
        this.store.select(selectSelectedReviewerId),
        this.store.select(selectSelectedReviewerName)
      ),
      switchMap(([
          action,
          generatedTasks,
          selectedPrototypeTasks,
          rootTemplate,
          templateTypes,
          selectedTemplateType,
          taskGenerationMetadataId,
          existingTaskIds,
          selectedReviewerId,
          selectedReviewerName,
        ]) => {
        // console.log('generatedTasks', generatedTasks);
        if (!generatedTasks || generatedTasks.length === 0) {
          return of(FandCActions.saveGeneratedTasksSuccess({ data: [] }));
        }

        const selectedTypeTemplateId = this.getTemplateIdFromSelectedType(templateTypes, selectedTemplateType);

        const normalizedTasks = generatedTasks.map((generatedTask: any) => {
          const existingTemplateId = generatedTask?.task?.templateId;
          if (existingTemplateId) {
            return generatedTask;
          }

          const generatedTaskName = generatedTask?.task?.name ?? generatedTask?.task?.taskName;
          const matchingPrototypeTask = (selectedPrototypeTasks || []).find((prototypeTask: any) =>
            prototypeTask?.taskName === generatedTaskName
          );

          const fallbackTemplateId =
            matchingPrototypeTask?.templateId ??
            selectedTypeTemplateId ??
            rootTemplate?.id ??
            null;

          return {
            ...generatedTask,
            templateId: generatedTask?.templateId ?? fallbackTemplateId,
            task: {
              ...generatedTask?.task,
              templateId: fallbackTemplateId,
            },
          };
        });

        // When existing tasks are present this is an edit; call the batch update endpoint instead.
        const isEditMode = !!taskGenerationMetadataId && Array.isArray(existingTaskIds) && existingTaskIds.length > 0;

        if (isEditMode) {
          return of(FandCActions.updateBatch());
        }

        const payload = {
          taskGenerationMetadataId: taskGenerationMetadataId ?? null,
          // Top-level batch override for the "Review" task's assignment (FAL-1237).
          // This envelope is rebuilt here (not forwarded verbatim from GetTasks), so the
          // reviewer selection must be re-applied at the top level; individual entries in
          // `tasks` are left untouched to preserve any resolved assignedUserId/assignedUserName.
          reviewerId: selectedReviewerId ?? null,
          reviewerName: selectedReviewerName ?? null,
          tasks: normalizedTasks,
        };

        return this.taskDataService.postGeneratedTasks(payload).pipe(
          map((result) => {
            const returnedData = result[1] ?? [];
            // console.log('postGeneratedTasks result', returnedData);
            return FandCActions.saveGeneratedTasksSuccess({
              data: returnedData,
            });
          })
        );
      })
    )
  );

  saveGenerationTask = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.saveGenerationMetadataSuccess),
      withLatestFrom(
        this.store.select(selectTaskId),
        this.store.select(selectedLoanNumber),
        this.store.select(selectSelectedReviewerId),
        this.store.select(selectSelectedReviewerName)
      ),
      switchMap(([
          metadata,
          selectTaskId,
          selectedLoanNumber,
          selectedReviewerId,
          selectedReviewerName,
        ]) => {

        // Only proceed if selectTaskId is null
        if (selectTaskId !== null) {
          return of(FandCActions.saveGenerationTaskSuccess({
            data: [{ task: { taskId: selectTaskId } }]
          }));
        }

        const metadataId = metadata.metadata.id;
        // FAL-1110: the initial F&C Creation task is created through the dedicated
        // multipart endpoint POST /api/Task/FinancialAndComplianceWorkflow, which resolves
        // the Creation template server-side. The UI must NOT resolve or send an initial
        // templateId and must NOT call GET /api/TaskTemplates to select it (rootTemplate/
        // templateTypes/selectedTemplateType are therefore intentionally not selected here).
        // prototypeTasks keep their own templateId for later standalone generation
        // (see updateBatch$/generateTasks$).
        let request: ITaskRequest = new TaskRequest();

        request.name = "Creation";
        request.referenceName = selectedLoanNumber;
        request.taskGenerationMetadataId = metadataId;
        request.referenceType = 'Facility';

        let formData : FormData = new FormData()
        formData.append('taskGenerationMetadataId', request.taskGenerationMetadataId);
        formData.append('name', request.name);
        formData.append('referenceName', request.referenceName);
        formData.append('requestJsonData', JSON.stringify({ loanNumber: selectedLoanNumber }));
        formData.append('referenceType', request.referenceType);

        if (selectedReviewerId != null) {
          // Task DTO (ITaskRequest) contract exposes assignedUserId/assignedUserName as the
          // canonical assignment fields; reviewerId/reviewerName are not part of that contract
          // and were being silently dropped server-side (issue #214).
          formData.append('assignedUserId', selectedReviewerId);
          formData.append('assignedUserName', selectedReviewerName ?? '');
        }

        return this.taskDataService.insertFinancialAndComplianceWorkflow(formData).pipe(
          map((result) => {
            const returnedData = result[1].data ?? [];
            const createdTaskId =
              Array.isArray(returnedData) && returnedData.length > 0
                ? returnedData[0]?.task?.taskId ?? null
                : null;

            if (createdTaskId) {
              this.store.dispatch(
                FandCActions.setTaskId({ taskId: createdTaskId })
              );
            }

            return FandCActions.saveGenerationTaskSuccess({
              data: returnedData,
            });
          }),
          catchError(error => {
            console.error('Error saving generation task', error);
            notify('Error saving generation task: ' + error.message, 'error', getNotificationTimeout());
            return of(FandCActions.saveGenerationTaskSuccess({
              data: []
            }));
          })
        );
      })
    )
  );

  updateBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.updateBatch),
      withLatestFrom(
        this.store.select(selectGeneratedTasks),
        this.store.select(selectSelectedPrototypeTasks),
        this.store.select(selectRootTemplate),
        this.store.select(selectTemplateTypes),
        this.store.select(selectSelectedTemplateType),
        this.store.select(selectTaskGenerationMetadataId),
        this.store.select(selectExistingTaskIds),
        this.store.select(selectSelectedLoanDetail),
      ),
      switchMap(([
        action,
        generatedTasks,
        selectedPrototypeTasks,
        rootTemplate,
        templateTypes,
        selectedTemplateType,
        taskGenerationMetadataId,
        existingTaskIds,
        selectedLoanDetail,
      ]) => {
        if (!taskGenerationMetadataId) {
          return of(FandCActions.updateBatchSuccess({ data: null }));
        }

        const selectedTypeTemplateId = this.getTemplateIdFromSelectedType(templateTypes, selectedTemplateType);

        // AC-5: derive the source-of-truth from selectedPrototypeTasks, not the stale preview grid.
        // When a prototype row already carries a taskId (edit-mode), use it directly.
        // When it does not, fall back to a name-match in generatedTasks (new-row path, preserves
        // the existing templateId-enrichment behaviour expected by pre-149 tests).
        const normalizedTasks = (selectedPrototypeTasks || []).map((protoTask: any) => {
          const fallbackTemplateId = protoTask?.templateId ?? selectedTypeTemplateId ?? rootTemplate?.id ?? null;
          if (protoTask?.taskId) {
            return {
              task: {
                taskId: protoTask.taskId,
                name: protoTask.taskName ?? protoTask.name,
                dueDate: protoTask.dueDate,
                alertDate: protoTask.alertDate,
                team: protoTask.team,
                assignedUserName: protoTask.assignedUserName,
                taskData: protoTask.taskData,
                taskTags: protoTask.taskTags ?? [],
                templateId: fallbackTemplateId,
              },
              templateId: fallbackTemplateId,
            };
          }
          // No taskId -- unconditional create; generatedTasks must not influence classification.
          const protoName = protoTask?.taskName ?? protoTask?.name;
          return {
            task: {
              taskId: null,
              name: protoName,
              dueDate: protoTask?.dueDate,
              alertDate: protoTask?.alertDate,
              team: protoTask?.team,
              assignedUserName: protoTask?.assignedUserName,
              taskData: protoTask?.taskData,
              taskTags: protoTask?.taskTags ?? [],
              templateId: fallbackTemplateId,
            },
            templateId: fallbackTemplateId,
          };
        });

        const existingIdSet = new Set(Array.isArray(existingTaskIds) ? existingTaskIds : []);
        // tasksToUpdate uses the flat shape the API expects, not the full IGeneratedTask wrapper
        const tasksToUpdate = normalizedTasks
          .filter((t: any) => t?.task?.taskId && existingIdSet.has(t.task.taskId))
          .map((t: any) => ({
            taskId: t.task.taskId,
            dueDate: t.task.dueDate,
            alertDate: t.task.alertDate,
            name: t.task.name ?? t.task.taskName,
            team: t.task.team,
            assignedUserName: t.task.assignedUserName,
            taskData: t.task.taskData,
            taskTags: t.task.taskTags ?? [],
          }));
        const tasksToCreate = normalizedTasks.filter((t: any) => !t?.task?.taskId || !existingIdSet.has(t.task.taskId));
        const presentIds = new Set(normalizedTasks.map((t: any) => t?.task?.taskId).filter(Boolean));
        const taskIdsToDelete = (Array.isArray(existingTaskIds) ? existingTaskIds : []).filter((id: string) => !presentIds.has(id));
        const updatedGenerationData = JSON.stringify({
          loanDetail: selectedLoanDetail,
          prototypeTasks: selectedPrototypeTasks,
        });

        const payload = { tasksToUpdate, tasksToCreate: tasksToCreate as any[], taskIdsToDelete, updatedGenerationData };

        return this.taskDataService.updateBatch(taskGenerationMetadataId, payload).pipe(
          map((result) => {
            const returnedData = result[1] ?? result ?? {};
            return FandCActions.updateBatchSuccess({ data: returnedData });
          }),
          catchError((error) => {
            console.error('Error updating batch:', error);
            notify('Error updating batch: ' + (error?.message ?? error), 'error', getNotificationTimeout());
            return of(FandCActions.updateBatchSuccess({ data: null }));
          })
        );
      })
    )
  );

  saveGenerationMetadata = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.saveGenerationMetadata),
      withLatestFrom(
        this.store.select(selectSelectedLoanDetail),
        this.store.select(selectLoans),
        this.store.select(selectedLoanNumber),
        this.store.select(selectSelectedPrototypeTasks),
        this.store.select(selectTaskGenerationMetadataId),
        this.store.select(selectSelectedReviewerId),
        this.store.select(selectSelectedReviewerName),
      ),
      switchMap(([
          action,
          selectedLoanDetail,
          loans,
          selectedLoan,
          selectSelectedPrototypeTasks,
          selectTaskGenerationMetadataId,
          selectedReviewerId,
          selectedReviewerName,
        ]) => {
          const selectedLoanName = this.getLoanName(selectedLoan);
          const fallbackFacilityId = this.findFacilityIdByLoanName(loans, selectedLoanName);

          const loanDetail$ = selectedLoanDetail
            ? of(selectedLoanDetail)
            : fallbackFacilityId
              ? this.taskDataService.getLoanDetail(fallbackFacilityId).pipe(
                map((response) => {
                  return response[1] ?? null;
                }),
                catchError(() => of(null))
              )
              : of(null);

          return loanDetail$.pipe(
            switchMap((resolvedLoanDetail) => {
              if (resolvedLoanDetail && !selectedLoanDetail) {
                this.store.dispatch(FandCActions.setLoanDetail({ loanDetail: resolvedLoanDetail }));
              }

              const metadata = {
                id: selectTaskGenerationMetadataId || null,
                generationData: JSON.stringify({
                  loanDetail: resolvedLoanDetail,
                  prototypeTasks: selectSelectedPrototypeTasks,
                  reviewerId: selectedReviewerId ?? null,
                  reviewerName: selectedReviewerName ?? null,
                })
              };

              return this.taskDataService.postGenerateTaskMetadata(metadata).pipe(
                map((result) => {
                  const returnedMetadata = result[1] ?? [];
                  return FandCActions.saveGenerationMetadataSuccess({
                    metadata: returnedMetadata,
                  });
                }),
                catchError(error => {
                  console.error('Error saving metadata:', error);
                  notify('Error saving metadata: ' + error.message, 'error', getNotificationTimeout());
                  return of(FandCActions.saveGenerationMetadataSuccess({
                    metadata: { id: selectTaskGenerationMetadataId || 'error' }
                  }));
                })
              );
            })
          );
      })
    )
  );


  fetchGenerationMetadata = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchGenerationMetadata),
      switchMap((action) => {

        return this.taskDataService.getGenerateTaskMetadata(action.metadataId).pipe(
          map((result) => {
            const responsePayload = result?.[1] ?? result ?? {};
            const returnedData = responsePayload?.result ?? responsePayload ?? {};
            const returnedMetadata = returnedData ?? {};
            // console.log('getGenerateTaskMetadata result', returnedMetadata);
            const generationDataRaw = returnedData?.generationData;
            if (generationDataRaw) {
              try {


              this.store.dispatch(
                FandCActions.setTaskGenerationMetadataId({ taskGenerationMetadataId: returnedData.id })
              );

              const existingTaskIds = Array.isArray(returnedData.existingTaskIds) ? returnedData.existingTaskIds : [];
              this.store.dispatch(FandCActions.setExistingTaskIds({ existingTaskIds }));

              const parsedData = typeof generationDataRaw === 'string'
                ? JSON.parse(generationDataRaw)
                : generationDataRaw;

              // Dispatch setLoanNumber action if loanNumber exists
              if (parsedData.loanDetail) {
                const metadataLoanDetail = Array.isArray(parsedData.loanDetail)
                  ? parsedData.loanDetail[0]
                  : parsedData.loanDetail;

                this.store.dispatch(
                FandCActions.setLoanDetail({ loanDetail: metadataLoanDetail })
                );

                const loanNameFromMetadata = this.getLoanName(metadataLoanDetail);
                if (loanNameFromMetadata) {
                  const loanIdFromMetadata = this.getLoanId(metadataLoanDetail) || '';
                  this.store.dispatch(
                    FandCActions.setLoanNumber({
                      selectedLoan: {
                        id: loanIdFromMetadata,
                        name: loanNameFromMetadata,
                      },
                    })
                  );
                }
              }


              // Dispatch setSelectedPrototypeTasks action if prototypeTasks exists
              if (parsedData.prototypeTasks) {
                this.store.dispatch(
                  FandCActions.setSelectedPrototypeTasks({
                    selectedPrototypeTasks: parsedData.prototypeTasks
                  })
                );

                // Restore form-level fields from the first prototype task
                if (parsedData.prototypeTasks.length > 0) {
                  const firstTask = parsedData.prototypeTasks[0];
                  const referenceDateCandidateKeys = [
                    'referenceDate',
                    'referencedate',
                    'reference_date',
                    'startDate',
                    'startdate',
                    'start_date',
                  ];
                  const maturityDateCandidateKeys = [
                    'untilDate',
                    'untildate',
                    'until_date',
                    'maturityDate',
                    'maturitydate',
                    'maturity_date',
                    'endDate',
                    'enddate',
                    'end_date',
                  ];
                  const reportEntityCandidateKeys = [
                    'reportEntity',
                    'reportentity',
                    'report_entity',
                    'reportEntityType',
                    'reportentitytype',
                    'report_entity_type',
                  ];
                  const reportEntityLabelCandidateKeys = [
                    'reportEntityLabel',
                    'reportentitylabel',
                    'report_entity_label',
                    'reportEntityName',
                    'reportentityname',
                    'report_entity_name',
                  ];

                  const firstTaskReferenceDate =
                    this.getPrototypeTaskField(firstTask, referenceDateCandidateKeys) ??
                    this.getFirstPrototypeTaskField(parsedData.prototypeTasks, referenceDateCandidateKeys) ??
                    this.getPrototypeTaskField(parsedData, referenceDateCandidateKeys);

                  const firstTaskMaturityDate =
                    this.getPrototypeTaskField(firstTask, maturityDateCandidateKeys) ??
                    this.getFirstPrototypeTaskField(parsedData.prototypeTasks, maturityDateCandidateKeys) ??
                    this.getPrototypeTaskField(parsedData, maturityDateCandidateKeys);

                  const firstTaskReportEntity =
                    this.getPrototypeTaskField(firstTask, reportEntityCandidateKeys) ??
                    this.getFirstPrototypeTaskField(parsedData.prototypeTasks, reportEntityCandidateKeys) ??
                    this.getPrototypeTaskField(parsedData, reportEntityCandidateKeys);

                  const firstTaskReportEntityLabel =
                    this.getPrototypeTaskField(firstTask, reportEntityLabelCandidateKeys) ??
                    this.getFirstPrototypeTaskField(parsedData.prototypeTasks, reportEntityLabelCandidateKeys) ??
                    this.getPrototypeTaskField(parsedData, reportEntityLabelCandidateKeys);

                  if (firstTask.type) {
                    this.store.dispatch(FandCActions.setSelectedTemplateType({ templateType: firstTask.type }));
                  }
                  if (firstTask.category) {
                    this.store.dispatch(FandCActions.setCategory({ category: firstTask.category }));
                    this.store.dispatch(FandCActions.fetchDefaultTasksForTemplate({ criteria: { templateName: firstTask.category } }));
                  }
                  if (firstTask.frequency) {
                    this.store.dispatch(FandCActions.setFrequency({ selectedFrequency: firstTask.frequency as Frequency }));
                  }
                  if (firstTask.format) {
                    this.store.dispatch(FandCActions.setFormatOption({ selectedFormatOption: firstTask.format }));
                  }
                  const resolvedReportEntity = this.resolveReportEntityValue(
                    firstTaskReportEntity,
                    firstTaskReportEntityLabel
                  );
                  const reportEntityFallback =
                    (firstTaskReportEntity ?? firstTaskReportEntityLabel ?? '').toString().trim() || null;
                  if (resolvedReportEntity || reportEntityFallback) {
                    this.store.dispatch(
                      FandCActions.setReportEntity({
                        selectedReportEntity: (resolvedReportEntity ?? reportEntityFallback) as any,
                      })
                    );
                  }
                  if (firstTask.entityName) {
                    this.store.dispatch(FandCActions.setEntityName({ selectedEntityName: firstTask.entityName }));
                  }
                  if (firstTaskReferenceDate) {
                    const parsedReferenceDate = new Date(firstTaskReferenceDate);
                    if (!isNaN(parsedReferenceDate.getTime())) {
                      this.store.dispatch(FandCActions.setReferenceDate({ selectedReferenceDate: parsedReferenceDate }));
                    }
                  }
                  if (firstTaskMaturityDate) {
                    const parsedMaturityDate = new Date(firstTaskMaturityDate);
                    if (!isNaN(parsedMaturityDate.getTime())) {
                      this.store.dispatch(FandCActions.setMaturityDate({ selectedMaturityDate: parsedMaturityDate }));
                    }
                  }
                  if (firstTask.due && firstTask.frequency) {
                    if (firstTask.frequency === 'One-Time') {
                      const dueDate = new Date(firstTask.due);
                      if (!isNaN(dueDate.getTime())) {
                        this.store.dispatch(FandCActions.setFrequencyOffsetValue({ selectedFrequencyOffsetValue: dueDate as any }));
                      }
                    } else {
                      const offsetOptions = this.frequencyOptionsService.getFrequencyOffsetOptions(firstTask.frequency as Frequency);
                      const normalizedDue = this.normalizeText(firstTask.due);
                      let matchingOption = offsetOptions.find((o: any) => {
                        const optionLabel = this.normalizeText(o?.label);
                        const optionValue = this.normalizeText(o?.value);
                        return optionLabel === normalizedDue || optionValue === normalizedDue;
                      });

                      if (!matchingOption) {
                        matchingOption = offsetOptions.find((o: any) => {
                          const optionLabel = this.normalizeText(o?.label);
                          const optionValue = this.normalizeText(o?.value);
                          return (
                            optionLabel.includes(normalizedDue) ||
                            normalizedDue.includes(optionLabel) ||
                            optionValue.includes(normalizedDue) ||
                            normalizedDue.includes(optionValue)
                          );
                        });
                      }

                      if (!matchingOption) {
                        const dueTokens = normalizedDue.split(' ').filter(Boolean);
                        matchingOption = offsetOptions.find((o: any) => {
                          const optionLabel = this.normalizeText(o?.label);
                          const matchedTokens = dueTokens.filter((token) => optionLabel.includes(token));
                          return dueTokens.length > 0 && matchedTokens.length / dueTokens.length >= 0.6;
                        });
                      }

                      if (matchingOption?.value != null) {
                        this.store.dispatch(
                          FandCActions.setFrequencyOffsetValue({
                            selectedFrequencyOffsetValue: matchingOption.value as any,
                          })
                        );
                      }
                    }
                  }
                }
              }

              if (parsedData.reviewerId != null || parsedData.reviewerName != null) {
                this.store.dispatch(FandCActions.setReviewer({
                  reviewerId: parsedData.reviewerId ?? null,
                  reviewerName: parsedData.reviewerName ?? null,
                }));
              }

              } catch (error) {
              console.error('Error parsing generationData:', error);
              }
            }


            return FandCActions.fetchGenerationMetadataSuccess({
              metadata: returnedMetadata,
            });
          }),
          catchError(() => of(FandCActions.fetchGenerationMetadataSuccess({ metadata: {} })))
        );
      })
    )
  );

  navigateOnPostSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FandCActions.saveGeneratedTasksSuccess, FandCActions.saveGenerationTaskSuccess, FandCActions.updateBatchSuccess),
        tap((result) => {
          try {
          // console.log('result', result);

          const taskIds = Array.isArray(result?.data)
            ? result.data.map((item: any) => item.task?.taskId)
            : [];

          // Filter taskIds to unique prefixes (e.g., FAC-134 from FAC-134-1, FAC-134-2)
          const uniquePrefixes = Array.from(new Set(
            taskIds
              .filter((id: string) => id && typeof id === 'string')
              .map((id: string) => {
                const match = id.match(/^([A-Z]+-\d+)/);
                return match ? match[1] : id;
              })
          ));

          const event = {
            taskIds:uniquePrefixes,
            filterTypeMessage: 'Filtered by created / updated task',
          };
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['/tasks'], { state: event });
            });
          } catch (error) {
            console.error('navigateOnPostSuccess$ error:', error);
          }
        })
      ),
    { dispatch: false } // Important: this effect does not dispatch a new action
  );

    setFrequencyOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.setFrequency),
      map(({ selectedFrequency }) => {
        try {
          const options = this.frequencyOptionsService.getFrequencyOffsetOptions(selectedFrequency);
          // console.log('Frequency Offset Options:', options);
          return FandCActions.setFrequencyOffsetOptions({ options });
        } catch {
          return FandCActions.setFrequencyOffsetOptions({ options: [] });
        }
      })
    )
  );

  fetchFrequencyOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchFrequencyOptions),
      map(() => {
        try {
          const options = this.frequencyOptionsService.getFrequencyOptions();
          // console.log('Frequency Options:', options);
          return FandCActions.setFrequencyOptions({ options });
        } catch {
          return FandCActions.setFrequencyOptions({ options: [] });
        }
      })
    )
  );

  fetchFormatOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FandCActions.fetchFormatOptions),
      map(() => {
        try {
          const options = [
            "Approved by Agent",
            "Audited",
            "Certified",
            "Certified, Audited",
            "Certified, Unaudited",
            "N/A",
            "Not Specified",
            "Satisfactory to Lender(s)",
            "Unaudited"
          ];
          return FandCActions.setFormatOptions({ options });
        } catch {
          return FandCActions.setFormatOptions({ options: [] });
        }
      })
    )
  );

setEntityNameOptions$ = createEffect(() =>
  this.actions$.pipe(
    ofType(FandCActions.setReportEntity),
    withLatestFrom(this.store.select(selectLoanDetail)),
    map(([action, loanDetail]) => {
      try {
        const selectedReportEntity = action.selectedReportEntity;
        const facilityDetail = loanDetail?.facilityDetail || {};
        let options: { label: string; value: string }[] = [];
        const rawEntityValue =
          selectedReportEntity && facilityDetail
            ? facilityDetail[selectedReportEntity]
            : null;

        if (selectedReportEntity && rawEntityValue != null) {
          const source = Array.isArray(rawEntityValue) ? rawEntityValue : [rawEntityValue];
          options = source
            .map((item: any) => {
              if (item == null) {
                return null;
              }
              if (typeof item === 'string' || typeof item === 'number') {
                const text = String(item).trim();
                return text ? { label: text, value: text } : null;
              }

              const label = String(item.name ?? item.label ?? item.value ?? '').trim();
              const value = String(item.id ?? item.value ?? item.name ?? '').trim();
              if (!label && !value) {
                return null;
              }

              const normalized = value || label;
              return { label: label || normalized, value: normalized };
            })
            .filter((x): x is { label: string; value: string } => !!x);
        }
        // console.log('Entity Name Options:', options);
        return FandCActions.setEntityNameOptions({ options });
      } catch {
        return FandCActions.setEntityNameOptions({ options: [] });
      }
    })
  )
);

  setFirstDueDate$ = createEffect(() =>
  this.actions$.pipe(
    ofType(FandCActions.setReferenceDate, FandCActions.setFrequencyOffsetValue),
    withLatestFrom(
      this.store.select(selectReferenceDate),
      this.store.select(selectedFrequency),
      this.store.select(selectFrequencyOffsetValue)
    ),
    map(([action, referenceDate, frequency, frequencyOffsetValue]) => {
      // console.log(referenceDate, frequency, frequencyOffsetValue);
      try {
        const firstDueDate = referenceDate && frequency && frequencyOffsetValue
          ? this.frequencyOptionsService.getFirstDueDate(
              frequency,
              frequencyOffsetValue,
              referenceDate
            )
          : null;
        // console.log('First Due Date:', firstDueDate);
        return FandCActions.setFirstDueDate({ firstDueDate });
      } catch {
        return FandCActions.setFirstDueDate({ firstDueDate: null });
      }
    })
  )
);
}
